if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        if (this.submitButton) {
          this.submitButtonText = this.submitButton.querySelector('span');
      }

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitting || this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.submitting = true;
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        let selectedVariant;
        try {
          selectedVariant = await this.prepareSelectedVariant();
          // Re-read after the asynchronous price confirmation, immediately before
          // constructing the request. Never trust an app's delayed hidden-ID write.
          const selection = this.readVariantSelection();
          if (selection && (!selectedVariant || String(selection.variant?.id) !== String(selectedVariant.id))) {
            throw new Error(window.canvoilStrings?.variantPriceError || 'Your options changed. Please try again.');
          }
        } catch (error) {
          if (this.isConnected) this.handleErrorMessage(error.message || window.cartStrings.error);
          this.finishSubmission();
          return;
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (selectedVariant) formData.set('id', selectedVariant.id);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        return fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json().then((data) => {
            if (!response.ok) data.status = data.status || response.status;
            return data;
          }))
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description || window.cartStrings.error);

              // A cart error can be temporary or a quantity limit, not only sold out.
              // Keep the server's message and allow a deliberate retry.
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);

            const renderCart = () => {
              try {
                CartPerformance.measure("add:paint-updated-sections", () => {
                  this.cart.renderContents(response);
                  this.cart.classList.remove('is-empty');
                });
              } catch (e) {
                console.error(e);
                window.location.href = window.routes.cart_url;
              }
            };
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(renderCart);
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              renderCart();
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(window.cartStrings.error);
          })
          .finally(() => {
            this.finishSubmission();

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      finishSubmission() {
        this.submitting = false;
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner')?.classList.add('hidden');
      }

      readVariantSelection() {
        // A popup owns its controls; a background product's Globo widget must
        // never determine the variant added by compact quick-add or an upsell.
        const scope = this.closest('.canvoil-quick-add, .product__info-container');
        if (!scope) return null;
        const pickers = Array.from(scope.querySelectorAll('variant-selects, variant-radios')).filter(control =>
          control.closest('.canvoil-quick-add, .product__info-container') === scope);
        const picker = pickers.find(control => control.getProductForm?.() === this.form
          || Array.from(control.querySelectorAll('[form]')).some(input => input.getAttribute('form') === this.form.getAttribute('id')));
        if (!picker && pickers.length) return null; // An independent upsell inside the same information column.
        const globo = Array.from(scope.querySelectorAll('.globo-swatch-product-detail')).find(widget => {
          const style = getComputedStyle(widget);
          return style.display !== 'none' && style.visibility !== 'hidden' && !widget.hidden
            && (widget.getClientRects().length || widget.querySelector('label, select')?.getClientRects().length);
        });
        if (!picker) {
          if (globo) throw new Error(window.canvoilStrings?.variantPriceError || 'We couldn’t verify these options. Please try again.');
          return null; // Single-variant and native upsell forms retain their behavior.
        }
        let variants;
        try {
          variants = typeof picker.getVariantData === 'function'
            ? picker.getVariantData()
            : JSON.parse(picker.querySelector('script[type="application/json"]').textContent);
        } catch (error) {
          throw new Error(window.canvoilStrings?.variantPriceError || 'We couldn’t verify these options. Please try again.');
        }
        if (!Array.isArray(variants) || !variants.length || !Array.isArray(variants[0].options)) {
          throw new Error(window.canvoilStrings?.variantPriceError || 'We couldn’t verify these options. Please try again.');
        }
        let options;
        if (globo) {
          const selected = new Map();
          globo.querySelectorAll('input[type="radio"]:checked, select').forEach(input => {
            const match = /^globo-option-(\d+)$/.exec(input.name);
            if (!match) return;
            const index = Number(match[1]);
            if (selected.has(index) && selected.get(index) !== input.value) {
              throw new Error(window.variantStrings?.unavailable || 'Unavailable');
            }
            selected.set(index, input.value);
          });
          options = Array.from({ length: variants[0].options.length }, (_, index) => selected.get(index));
        } else {
          options = Array.from(picker.querySelectorAll('select, fieldset'), control =>
            control.tagName === 'SELECT' ? control.value : control.querySelector('input:checked')?.value);
        }
        const complete = options.length === variants[0].options.length && options.every(value => typeof value === 'string' && value.length);
        const variant = complete && variants.find(candidate => candidate.options.length === options.length
          && candidate.options.every((value, index) => value === options[index]));
        return { scope, picker, globo, options, variant, signature: JSON.stringify(options) };
      }

      async prepareSelectedVariant() {
        // Option apps can rebuild controls or finish a previous selection while a
        // request is pending. Re-resolve the live tuple rather than selecting a fallback.
        for (let attempt = 0; attempt < 5; attempt++) {
          const selection = this.readVariantSelection();
          if (!selection) return null;
          const { picker, globo, variant, signature, scope } = selection;
          if (!variant) throw new Error(window.variantStrings?.unavailable || 'Unavailable');
          if (!variant.available) throw new Error(window.variantStrings?.soldOut || 'Sold out');
          const compact = Boolean(this.closest('.canvoil-quick-add'));
          const price = scope.querySelector('.canvoil-quick-add__price, [id^="price-"]');
          const state = this.form.dataset.canvoilVariantState;
          const matchingId = String(this.variantIdInput.value) === String(variant.id);
          const matchingPrice = this.confirmedVariantSignature === signature && String(picker.currentVariant?.id) === String(variant.id);
          const needsConfirmation = !matchingId || (state && state !== 'ready')
            || price?.classList.contains('visibility-hidden') || price?.getAttribute('aria-busy') === 'true'
            || (globo && !matchingPrice);
          if (!needsConfirmation) return variant;
          // Compact quick-add already owns a separate scoped async price request.
          // It disables submit until that response settles; do not invoke the
          // background/native section renderer with its namespaced form IDs.
          if (compact || typeof picker.renderProductInfo !== 'function' || picker.getProductForm?.() !== this.form) {
            throw new Error(window.canvoilStrings?.variantPriceError || 'We couldn’t verify these options. Please try again.');
          }
          picker.currentVariant = variant;
          picker.updateVariantInput();
          await picker.renderProductInfo();
          if (!this.isConnected) throw new Error(window.cartStrings.error);
          const current = this.readVariantSelection();
          if (!current || current.picker !== picker || current.signature !== signature) continue;
          if (this.form.dataset.canvoilVariantState === 'sold-out') {
            throw new Error(window.variantStrings?.soldOut || 'Sold out');
          }
          if (this.form.dataset.canvoilVariantState !== 'ready' || String(this.variantIdInput.value) !== String(variant.id)) {
            throw new Error(window.canvoilStrings?.variantPriceError || 'We couldn’t verify these options. Please try again.');
          }
          this.confirmedVariantSignature = signature;
          return variant;
        }
        throw new Error(window.canvoilStrings?.variantPriceError || 'Your options changed. Please try again.');
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
